import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { api, errorText } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const FIELD = "w-full rounded-xl border border-ink/15 bg-white px-3.5 py-2.5 text-sm text-ink outline-none focus:border-ink";

const AddStaff = ({ onAdded }) => {
  const [form, setForm] = useState({ email: "", name: "", role: "staff", password: "" });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/staff", { ...form, password: form.password || null });
      toast.success("Access added");
      setForm({ email: "", name: "", role: "staff", password: "" });
      onAdded();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={add} className="grid gap-3 rounded-2xl border border-ink/10 bg-white p-5 md:grid-cols-[1.4fr_1fr_1fr_auto_auto]" data-testid="add-staff-form">
      <input type="email" required placeholder="Email (Google or work email)" value={form.email} onChange={set("email")} className={FIELD} data-testid="staff-email-input" />
      <input placeholder="Name" value={form.name} onChange={set("name")} className={FIELD} data-testid="staff-name-input" />
      <input type="password" minLength={8} placeholder="Password (optional)" value={form.password} onChange={set("password")} className={FIELD} data-testid="staff-password-input" />
      <select value={form.role} onChange={set("role")} className={FIELD} data-testid="staff-role-select">
        <option value="staff">Staff</option><option value="admin">Owner</option>
      </select>
      <button type="submit" disabled={busy} className="btn-primary !py-2.5" data-testid="staff-add-button"><Plus className="h-4 w-4" /> Add</button>
    </form>
  );
};

export const StaffTab = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/staff").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body, msg) => {
    try { await api.patch(`/admin/staff/${id}`, body); toast.success(msg); load(); } catch (e) { toast.error(errorText(e)); }
  };
  const setPassword = (s) => {
    const pw = window.prompt(`New password for ${s.email} (at least 8 characters)`);
    if (pw) patch(s.id, { password: pw }, "Password updated");
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove access for ${s.email}?`)) return;
    try { await api.delete(`/admin/staff/${s.id}`); toast.success("Access removed"); load(); } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <div className="grid gap-4">
      <p className="text-sm text-ink/70">Only people listed here can sign in. They can use <strong>Continue with Google</strong> with this email, or email and password if you set one. Owners can also manage this list.</p>
      <AddStaff onAdded={load} />
      <div className="divide-y divide-ink/10 rounded-2xl border border-ink/10 bg-white" data-testid="staff-list">
        {items.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`staff-row-${s.email}`}>
            <div className="grid h-10 w-10 place-items-center overflow-hidden rounded-full bg-ink text-sm font-semibold text-canvas">
              {s.picture ? <img src={s.picture} alt="" className="h-full w-full object-cover" /> : (s.name || s.email)[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-ink">{s.name || s.email} {s.id === user?.id && <span className="text-xs font-normal text-ink/55">(you)</span>}</p>
              <p className="truncate text-xs text-ink/60">{s.email} · {s.has_password ? "Google or password" : "Google only"}</p>
            </div>
            <select value={s.role} disabled={s.id === user?.id} onChange={(e) => patch(s.id, { role: e.target.value }, "Role updated")}
              className="rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold" data-testid={`staff-role-${s.email}`}>
              <option value="staff">Staff</option><option value="admin">Owner</option>
            </select>
            <button onClick={() => setPassword(s)} className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-muted" aria-label="Set password" data-testid={`staff-password-${s.email}`}><KeyRound className="h-4 w-4" /></button>
            {s.id !== user?.id && <button onClick={() => remove(s)} className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-muted" aria-label="Remove" data-testid={`staff-remove-${s.email}`}><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
};
